import { useCallback, useMemo } from 'react';
import { useSearchParams } from './use-search-params';

interface UseSearchParamsStateOptions<T> {
  defaultValue: T | (() => T);
  serialize: (val: T) => string;
  deserialize?: (val: string) => T;
}

export function useSearchParamsState<T>(key: string, options: UseSearchParamsStateOptions<T>) {
  const search = useSearchParams();

  const { defaultValue, serialize = JSON.stringify, deserialize = JSON.parse } = options;

  const value = useMemo(() => {
    const query = search.get(key);

    if (query !== undefined) {
      return deserialize(query) as T;
    }

    return defaultValue instanceof Function ? defaultValue() : defaultValue;
  }, [search, key, defaultValue, deserialize]);

  const setValue = useCallback(
    (val: T | ((prevVal: T) => T)) => {
      if (val instanceof Function) {
        search.set(key, serialize(val(value)));
        return;
      }

      search.set(key, serialize(val));
    },
    [key, search, serialize, value],
  );

  return [value, setValue] as const;
}
